import {
  createFileRoute,
  Link,
  notFound,
  redirect,
} from '@tanstack/react-router'
import { useEffect } from 'react'
import { useTranslations } from 'use-intl'

import { NotFoundPage } from '@/components/not-found'
import { ArticlePage, type Post } from '@/features/blog/components/article-page'
import { type PostCardData } from '@/features/blog/components/post-card'
import { previewClient, studioToken } from '@/sanity/preview'
import { postQuery, relatedPostsQuery } from '@/sanity/queries'

// Editor-only draft preview. Client-only so the loader can read the Studio
// session from localStorage and fetch drafts *as the editor*: Sanity refuses
// anyone who isn't a project member, so nothing here needs its own auth.
// Drafts never reach /blog, the public article route or the sitemap (public
// client, no token).
export const Route = createFileRoute('/$locale/blog/preview/$slug')({
  ssr: false,
  loader: async ({ params }) => {
    // Studio auth is either a localStorage token or a Sanity cookie
    // (browser-dependent): ask the API who we are instead of guessing.
    // Unauthenticated returns `{}`, never an error.
    const client = previewClient(studioToken())
    const me = await client.request<{ id?: string }>({ uri: '/users/me' })
    if (!me?.id) throw redirect({ href: '/studio' })
    const [post, related] = await Promise.all([
      client.fetch<Post | null>(postQuery(params.locale), {
        slug: params.slug,
      }),
      client.fetch<PostCardData[]>(relatedPostsQuery(params.locale), {
        slug: params.slug,
      }),
    ])
    if (!post) throw notFound()
    return { post, related }
  },
  notFoundComponent: () => <NotFoundPage />,
  head: () => ({ meta: [{ name: 'robots', content: 'noindex, nofollow' }] }),
  component: PreviewPost,
})

function PreviewPost() {
  const { post, related } = Route.useLoaderData()
  const { locale, slug } = Route.useParams()
  const t = useTranslations('blog')
  // Fixed banner above the sticky header; styles.css offsets body + header
  // while `data-preview` is set.
  useEffect(() => {
    document.documentElement.dataset.preview = ''
    return () => {
      delete document.documentElement.dataset.preview
    }
  }, [])
  return (
    <>
      <div className="fixed inset-x-0 top-0 z-50 flex h-9 items-center justify-center gap-x-4 overflow-x-auto bg-amber-100 px-4 text-sm whitespace-nowrap text-amber-900">
        <span>{t('previewBanner')}</span>
        {post.otherSlug && (
          <Link
            to="/$locale/blog/preview/$slug"
            params={{
              locale: locale === 'fr' ? 'en' : 'fr',
              slug: post.otherSlug,
            }}
            className="underline"
          >
            {t('previewOtherLocale')}
          </Link>
        )}
      </div>
      <ArticlePage
        post={post}
        related={related}
        locale={locale}
        slug={slug}
        preview
      />
    </>
  )
}
